import { useState } from 'react';
import { useNumbers } from '../../lib/api/hooks-tms.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, StatusBadge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import { api } from '../../lib/api/client.js';
import { useQueryClient } from '@tanstack/react-query';
import { tmsKeys } from '../../lib/api/hooks-tms.js';
import type { Destination } from '@arcnet/contracts';

/**
 * Inbound numbers and where each one lands.
 *
 * The route is edited inline: a DID with no destination is the single most
 * common cause of "our new number does not work", so it is shown on the same
 * row as the number rather than a click away.
 */
export function NumbersTab({ tenantId }: { tenantId: string }) {
  const numbers = useNumbers(tenantId);
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Destination | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async (numberId: string) => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.put(`/v1/tenants/${tenantId}/numbers/${numberId}/route`,
        { destination: draft, record_calls: false, is_enabled: true });
      await qc.invalidateQueries({ queryKey: tmsKeys.numbers(tenantId) });
      setEditing(null);
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Inbound numbers</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Where each DID lands when someone calls it.
          </p>
        </div>
      </div>

      {numbers.error && <ErrorBanner error={numbers.error} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Number</th><th>Type</th><th>Carrier</th><th>Status</th>
                <th>Routes to</th><th />
              </tr>
            </thead>
            {numbers.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {numbers.data?.data.map((n) => (
                  <tr key={n.id}>
                    <td className="arc-mono" style={{ fontWeight: 600 }}>{n.e164}</td>
                    <td><Badge tone="muted">{n.number_type.replace(/_/g, ' ')}</Badge></td>
                    <td className="arc-muted">{n.carrier_name ?? '—'}</td>
                    <td><StatusBadge status={n.status} /></td>
                    <td>
                      {editing === n.id ? (
                        <DestinationPicker tenantId={tenantId} value={draft} onChange={setDraft} />
                      ) : n.route ? (
                        <DestinationLabel tenantId={tenantId} destination={n.route.destination} />
                      ) : (
                        <span className="arc-badge arc-badge-warn">
                          <Icon name="warning" size={10} /> no route
                        </span>
                      )}
                    </td>
                    <td className="arc-td-right">
                      {editing === n.id ? (
                        <div className="arc-row" style={{ justifyContent: 'flex-end', gap: 4 }}>
                          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
                            disabled={!draft || saving} onClick={() => void save(n.id)}>
                            {saving ? '…' : 'Save'}
                          </button>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            onClick={() => setEditing(null)}>Cancel</button>
                        </div>
                      ) : (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-row-actions"
                          onClick={() => { setEditing(n.id); setDraft(n.route?.destination ?? null); }}>
                          {n.route ? 'Change' : 'Set route'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!numbers.isLoading && numbers.data?.data.length === 0 && (
          <Empty icon="hash" title="No numbers assigned"
            hint="Assign a DID from platform inventory to give this account an inbound number." />
        )}
      </div>
    </>
  );
}
